import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import ConsoleHrozontalHeadTable from "../-components/ConsolehorizontalHeadTable";
import LoadingPop from "components/popup/LoadingPop";
import { toastErrorSyle } from "hooks/utils/util";
import { usePostConsoleDriverSchedule, usePutConsoleDriverSchedule } from "hooks/console/useConsoleDriverSchedule";
import { useGetConsoleWarehouses } from "hooks/console/useConsoleWarehouse";
import { useGetConsoleDrivers } from "hooks/console/useConsoleDriver";
import { ReactComponent as ArrowDown } from "assets/morning/icon/arrowDownNavy.svg";

// 백엔드 안내 메시지를 견고하게 추출 (axios 에러 본문 / 일반 Error 모두 대응)
const getErrMsg = (error: any, fallback: string) =>
    error?.response?.data?.message || error?.message || fallback;

export type DriverScheduleFormState = {
    schedule_no: number | null;
    schedule_nos: number[]; // 수정 모드: 기존 묶음에 포함된 모든 schedule_no
    warehouse_no: string;
    driver_no: string;
    work_date: string; // 대표일(하위호환)
    work_start_date: string; // 기간 시작일
    work_end_date: string; // 기간 종료일
};

export default function DriverScheduleForm({
    form,
    setForm,
    update,
    setEditMode,
}: {
    form: DriverScheduleFormState;
    setForm: React.Dispatch<React.SetStateAction<DriverScheduleFormState>>;
    update?: boolean;
    setEditMode?: React.Dispatch<React.SetStateAction<boolean>>;
}) {
    const navigate = useNavigate();
    const { mutate: createSchedule, isPending } = usePostConsoleDriverSchedule();
    const { mutate: updateSchedule, isPending: isPendingUpdate } = usePutConsoleDriverSchedule();
    // 셀렉트 옵션 (전체 조회)
    const { data: warehouseData } = useGetConsoleWarehouses({ limit: 1000, page: 1 });
    const { data: driverData } = useGetConsoleDrivers({ limit: 1000, page: 1 });

    // 항구명 먼저 선택 → 해당 항구의 입고지만 선택 (driver-info 동일 패턴)
    const [portName, setPortName] = useState("");
    const warehouses = warehouseData?.data_list ?? [];
    // 항구명/입고지명 옵션 — 화면 간 정렬 통일 (한글 가나다순)
    const portOptions = Array.from(new Set(warehouses.map(w => w.port_name).filter(Boolean))).sort((a, b) => a.localeCompare(b, "ko"));
    const filteredWarehouses = warehouses
        .filter(w => w.port_name === portName)
        .sort((a, b) => a.name.localeCompare(b.name, "ko"));

    // 수정 모드: 프리필된 입고지의 항구명으로 항구 셀렉트 초기화
    useEffect(() => {
        if (form.warehouse_no && !portName) {
            const w = warehouses.find(x => String(x.warehouse_no) === form.warehouse_no);
            if (w?.port_name) setPortName(w.port_name);
        }
    }, [form.warehouse_no, warehouses]);

    const onSubmit = (e: FormEvent) => {
        e.preventDefault();
        if (!form.warehouse_no) return toast.error("입고지명을 선택해주세요.", toastErrorSyle);
        if (!form.driver_no) return toast.error("담당기사를 선택해주세요.", toastErrorSyle);

        // 공통: 기간 검증
        if (!form.work_start_date || !form.work_end_date) return toast.error("기간을 선택해주세요.", toastErrorSyle);
        if (form.work_start_date > form.work_end_date)
            return toast.error("종료일은 시작일보다 빠를 수 없습니다.", toastErrorSyle);

        // 수정 모드: 기존 묶음을 새 기간으로 교체
        if (update && form.schedule_no) {
            updateSchedule(
                {
                    warehouse_no: form.warehouse_no,
                    driver_no: form.driver_no,
                    work_start_date: form.work_start_date,
                    work_end_date: form.work_end_date,
                    schedule_nos: form.schedule_nos,
                    schedule_no: String(form.schedule_no),
                },
                {
                    onSuccess: () => {
                        toast.success("성공적으로 수정 되었습니다.");
                        setEditMode?.(false);
                    },
                    onError: (error: any) => toast.error(getErrMsg(error, "수정에 실패했습니다."), toastErrorSyle),
                },
            );
            return;
        }

        // 등록 모드: 기간(시작일~종료일)으로 일별 다건 생성
        createSchedule(
            {
                warehouse_no: form.warehouse_no,
                driver_no: form.driver_no,
                work_start_date: form.work_start_date,
                work_end_date: form.work_end_date,
            },
            {
                onSuccess: (data: any) => {
                    toast.success("성공적으로 등록 되었습니다.");
                    const newNo = data?.data?.schedule_no;
                    navigate(newNo ? `/morning/customer/driver-schedule/${newNo}` : "/morning/customer/driver-schedule");
                },
                onError: (error: any) => toast.error(getErrMsg(error, "등록에 실패했습니다."), toastErrorSyle),
            },
        );
    };

    const selectTrigger =
        "relative shadow-none text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2 w-full max-w-[400px] border border-[#E1E2E4] bg-white h-[44px]";

    return (
        <>
            {isPending || isPendingUpdate ? (
                <LoadingPop />
            ) : (
                <form onSubmit={onSubmit}>
                    <div className="mt-[8px] mb-[40px]">
                        <ConsoleHrozontalHeadTable
                            thead={[
                                {
                                    title: "항구명",
                                    key: "port_name",
                                    content: () => (
                                        <Select
                                            value={portName}
                                            onValueChange={value => {
                                                setPortName(value);
                                                setForm(prev => ({ ...prev, warehouse_no: "" })); // 항구 변경 시 입고지 초기화
                                            }}
                                        >
                                            <SelectTrigger className={selectTrigger}>
                                                <SelectValue placeholder="항구명을 선택해주세요." />
                                                <ArrowDown className="[&_path]:fill-[#666666]" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white">
                                                {portOptions.map(port => (
                                                    <SelectItem key={port} value={port}>
                                                        {port}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ),
                                },
                                {
                                    title: "입고지명",
                                    key: "warehouse_no",
                                    content: () => (
                                        <Select
                                            value={form.warehouse_no}
                                            onValueChange={value => setForm(prev => ({ ...prev, warehouse_no: value }))}
                                            disabled={!portName}
                                        >
                                            <SelectTrigger className={`${selectTrigger} disabled:bg-[#f4f4f4] disabled:cursor-not-allowed`}>
                                                <SelectValue placeholder={portName ? "입고지명을 선택해주세요." : "항구명을 먼저 선택해주세요."} />
                                                <ArrowDown className="[&_path]:fill-[#666666]" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white">
                                                {filteredWarehouses.map(item => (
                                                    <SelectItem key={item.warehouse_no} value={String(item.warehouse_no)}>
                                                        {item.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ),
                                },
                                {
                                    title: "담당기사",
                                    key: "driver_no",
                                    content: () => (
                                        <Select
                                            value={form.driver_no}
                                            onValueChange={value => setForm(prev => ({ ...prev, driver_no: value }))}
                                        >
                                            <SelectTrigger className={selectTrigger}>
                                                <SelectValue placeholder="담당기사를 선택해주세요." />
                                                <ArrowDown className="[&_path]:fill-[#666666]" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white">
                                                {driverData?.data_list.map(item => (
                                                    <SelectItem key={item.driver_no} value={String(item.driver_no)}>
                                                        {item.name} ({item.phone})
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ),
                                },
                                {
                                    title: "기간",
                                    key: "work_date",
                                    content: () => (
                                        <div className="flex items-center gap-[8px] w-full max-w-[400px]">
                                            <input
                                                type="date"
                                                value={form.work_start_date}
                                                max={form.work_end_date || undefined}
                                                onChange={e =>
                                                    setForm(prev => ({ ...prev, work_start_date: e.target.value }))
                                                }
                                                onClick={e => {
                                                    try {
                                                        (e.currentTarget as HTMLInputElement).showPicker?.();
                                                    } catch {}
                                                }}
                                                className="border border-[#E1E2E4] rounded-[5px] px-3 h-[44px] text-[16px] w-full cursor-pointer"
                                            />
                                            <span className="text-[#666666]">~</span>
                                            <input
                                                type="date"
                                                value={form.work_end_date}
                                                min={form.work_start_date || undefined}
                                                onChange={e =>
                                                    setForm(prev => ({ ...prev, work_end_date: e.target.value }))
                                                }
                                                onClick={e => {
                                                    try {
                                                        (e.currentTarget as HTMLInputElement).showPicker?.();
                                                    } catch {}
                                                }}
                                                className="border border-[#E1E2E4] rounded-[5px] px-3 h-[44px] text-[16px] w-full cursor-pointer"
                                            />
                                        </div>
                                    ),
                                },
                            ]}
                            tbody={form}
                        />

                        <div className="flex justify-center gap-2 mt-5">
                            <button className="bg-black text-white rounded-[5px] px-4 py-2">저장</button>
                            <button
                                type="button"
                                className="bg-black text-white rounded-[5px] px-4 py-2"
                                onClick={() => (update && setEditMode ? setEditMode(false) : navigate(-1))}
                            >
                                {update ? "취소" : "목록"}
                            </button>
                        </div>
                    </div>
                </form>
            )}
            <Toaster position="bottom-center" reverseOrder={false} />
        </>
    );
}
